import { fetcher } from '@commenda/utils';

interface IUploadedFile {
  fileUrl: string;
  fileName: string;
  downloadUrl: string;
}

// Returns undefined without a scope so the uploaders fall back to their authenticated path.
export function buildShareScopedFileUpload(
  shareToken: string | undefined
): ((file: File) => Promise<IUploadedFile | null>) | undefined {
  if (!shareToken) return undefined;

  // Null on failure, never throw: the uploaders read the return value alone, so a throw
  // leaves the field spinning with no error.
  return async (file: File): Promise<IUploadedFile | null> => {
    const sanitizedFileName = file.name.replaceAll(
      /[^a-zA-Z0-9!\-_.*'()]/g,
      '_'
    );

    try {
      const presignResponse = await fetcher('s3/signedUrlForPut', {
        method: 'POST',
        body: { fileName: sanitizedFileName },
        shareToken,
      });

      if (!presignResponse.ok) {
        console.error('Failed to get a signed upload url for the shared form.');
        return null;
      }

      const { signedUrl, hostedUrl, signedGetUrl } =
        (await presignResponse.json()) as {
          signedUrl: string;
          hostedUrl: string;
          signedGetUrl: string;
        };

      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        console.error('Failed to upload the file for the shared form.');
        return null;
      }

      return {
        fileUrl: hostedUrl,
        fileName: file.name,
        downloadUrl: signedGetUrl,
      };
    } catch (error) {
      console.error('Shared-form upload failed:', error);
      return null;
    }
  };
}
